import type { ClosureReason, OpenDetail, Pool, PoolSection, PoolStatus, ReopenEstimate } from "../../shared/types";
import { CLOSED_SEASON_BADGE, CLOSED_SEASON_NOTE, GEO_DENIED_COPY, GEO_LABEL_IDLE, GEO_LABEL_LOCATING, GEO_LABEL_LOCATED, addressHtml, esc, favToggleHtml, footerHtml, geoButtonHtml, openDetailPickerHtml, paintSectionStatus, reasonPickerHtml, renderTimeline, reopenPickerHtml, sectionStatusView, slideView, slopedEntryBadgeHtml, statusLineHtml } from "../util";
import { cachedStatus, loadAllStatuses, loadPools } from "../store";
import { getRecentReports } from "../api";
import { enrichClosedReport, reportSection } from "../report";
import { navigate } from "../router";
import { getFavorites, toggleFavorite } from "../favorites";
import { trackAction } from "../datadog";
import {
  type LatLng,
  cachedLocation,
  getUserLocation,
  hasLocationOptIn,
  lastLocationError,
  milesAwayLabel,
  nearestPool,
  poolDistanceMeters,
  sortPools,
} from "../geo";
import {
  type Filters,
  type SummaryCounts,
  filtersToHash,
  matchesQuery,
  matchesStaticFilters,
  parseFilters,
  poolIsOpenNow,
  summarizeOpen,
} from "../filters";
import { CITY } from "../city";
import { searchBoxHtml, skeletonListHtml, wireSearchBox } from "./chrome";

/**
 * The three top-bar tabs. The `list` value (and its `#/` hash) is unchanged; only
 * its display label reads "Pools". Splash Pads is filter-less; the pool tabs
 * carry filters.
 */
export type TabName = "list" | "map" | "splash";

/**
 * Shared top-bar tab nav, rendered identically by every view (Pools, Splash
 * Pads, Map) so the active tab always follows the current route. The pool views
 * (Pools/Map) carry the active filters in their hashes; Splash Pads is
 * filter-less so it always links to plain `#/splash`. Display order is
 * Pools, Splash Pads, Map (Map last).
 */
export function viewTabs(active: TabName, filters: Filters): string {
  const tab = (name: TabName, href: string, label: string) =>
    `<a class="tab${active === name ? " tab-active" : ""}" href="${href}">${label}</a>`;
  // The kiddie-focused "Pools" tab is gated on CITY.features.pools — Arlington
  // (spraygrounds only) hides it and leads with the Splash tab. The splash tab's
  // label is city-specific ("Splash Pads" vs "Spraygrounds").
  return `
      <nav class="view-tabs">
        ${CITY.features.pools ? tab("list", filtersToHash(filters, "list"), "Pools") : ""}
        ${tab("splash", "#/splash", CITY.splashTabLabel)}
        ${tab("map", filtersToHash(filters, "map"), "Map")}
      </nav>`;
}

/**
 * One reportable section tile (Direction 1 — Confidence-led). Renders a soft tile
 * whose left accent + status word are painted from live status by
 * `paintSectionStatus`. `showLabel` controls the "Main pool" / "Children's pool"
 * heading: it's shown only when the card carries ≥2 sections, and hidden on a
 * single-section card (Francis-style) to cut noise.
 */
function sectionCard(pool: Pool, section: PoolSection, showLabel: boolean): string {
  const label = section === "kiddie" ? "Children's pool" : "Main pool";
  return `
    <div class="section" data-pool="${esc(pool.poolId)}" data-section="${section}">
      ${showLabel ? `<div class="section-head"><span class="section-label">${label}</span></div>` : ""}
      ${statusLineHtml()}
      <p class="hero" data-role="freshness"></p>
      <div class="report-buttons">
        <button class="btn btn-open" data-action="report" data-status="open">It's open</button>
        <button class="btn btn-closed" data-action="report" data-status="closed">It's closed</button>
      </div>
      ${reasonPickerHtml()}
      ${reopenPickerHtml()}
      ${openDetailPickerHtml()}
      <details class="card-history">
        <summary>Recent reports</summary>
        <ol class="timeline" data-role="timeline"><li class="muted">Loading…</li></ol>
      </details>
    </div>`;
}

/**
 * The water-slide section on a waterslide pool's card (Deanwood & Barry Farm).
 * The slide defaults OFF; "Slide running / Slide off" reports the `slide` section
 * (open = running/On, closed = Off). At parity with the kiddie/main sections, a
 * "Slide off" report now reveals the shared reason + reopen pickers and the
 * section carries a recent-reports history — the report buttons, pickers, and
 * history `<details>` all reuse the same data-role hooks the kiddie card wires.
 *
 * Exception: a closed-for-season pool's slide is informational only — it renders
 * a fixed "Off · closed for season" badge with disabled (un-wired) buttons.
 */
function slideSection(pool: Pool): string {
  if (pool.closedForSeason) {
    return `
    <div class="section section-slide sec-acc-neutral" data-pool="${esc(pool.poolId)}" data-section="slide">
      <div class="section-head"><span class="section-label">🛝 Water slide</span></div>
      <p class="status-line"><span class="status status-neutral" data-role="status">Off</span> <span class="qual" data-role="qual">· closed for season</span></p>
      <div class="report-buttons">
        <button class="btn btn-open" disabled>Running</button>
        <button class="btn btn-closed" disabled>Closed for the season</button>
      </div>
    </div>`;
  }
  return `
    <div class="section section-slide sec-acc-neutral" data-pool="${esc(pool.poolId)}" data-section="slide">
      <div class="section-head"><span class="section-label">🛝 Water slide</span></div>
      <p class="status-line"><span class="status status-neutral" data-role="status">Off</span> <span class="qual" data-role="qual">· usually off</span></p>
      <p class="hero" data-role="freshness">Tap if it's running</p>
      <div class="report-buttons">
        <button class="btn btn-open" data-action="report" data-status="open">Running</button>
        <button class="btn btn-closed" data-action="report" data-status="closed">Off</button>
      </div>
      ${reasonPickerHtml()}
      ${reopenPickerHtml()}
      <details class="card-history">
        <summary>Recent reports</summary>
        <ol class="timeline" data-role="timeline"><li class="muted">Loading…</li></ol>
      </details>
    </div>`;
}

/**
 * A pool that is closed for the whole season (renovation): no status badge,
 * report buttons, or hours — just a clear "Closed for the season" notice so a
 * family knows not to make the trip. Still a link/favoritable like any card.
 */
function closedPoolCard(pool: Pool): string {
  return `
    <article class="pool-card pool-card-closed" data-pool-card="${esc(pool.poolId)}">
      <div class="card-head">
        <h2><a class="pool-link" href="#/pool/${encodeURIComponent(pool.poolId)}">${esc(pool.name)}</a></h2>
        ${favToggleHtml(pool.poolId)}
      </div>
      <p class="addr">${addressHtml(pool.address)}</p>
      <p class="closed-season"><span class="badge badge-unknown">${CLOSED_SEASON_BADGE}</span></p>
      <p class="closed-season-note">${CLOSED_SEASON_NOTE}</p>
      ${CITY.features.slides && pool.hasWaterslide ? slideSection(pool) : ""}
    </article>`;
}

/**
 * "0.4 mi away" line for a card, shown only when a location fix exists and the
 * pool has usable coordinates (a non-finite distance renders nothing). Pure
 * string helper so it stays re-render-safe.
 * Exported for unit testing; not part of the public rendering API.
 */
export function distanceLine(pool: Pool, loc: LatLng | null): string {
  if (!loc) return "";
  const meters = poolDistanceMeters(pool, loc);
  if (!Number.isFinite(meters)) return "";
  return `<p class="card-distance">${milesAwayLabel(meters)}</p>`;
}

function poolCard(pool: Pool, loc: LatLng | null = null): string {
  if (pool.closedForSeason) return closedPoolCard(pool);
  // Kiddie pools render BOTH the Main section and the Children's section, Main
  // first (a kiddie facility still has the big pool). Main-only pools render just
  // the Main section. A `childrenOnly` pool's whole basin IS the kiddie section,
  // so it renders only the Children's tile (no Main). Section labels are shown
  // only when the card has ≥2 sections (main + kiddie and/or a water slide) — a
  // lone section needs no heading.
  const showSlide = CITY.features.slides && pool.hasWaterslide;
  const sectionCount =
    (pool.childrenOnly ? 0 : 1) + (pool.hasKiddiePool ? 1 : 0) + (showSlide ? 1 : 0);
  const showLabel = sectionCount >= 2;
  return `
    <article class="pool-card" data-pool-card="${esc(pool.poolId)}">
      <div class="card-head">
        <h2><a class="pool-link" href="#/pool/${encodeURIComponent(pool.poolId)}">${esc(pool.name)}</a></h2>
        ${favToggleHtml(pool.poolId)}
      </div>
      <p class="addr">${addressHtml(pool.address)}</p>
      ${pool.slopedEntry ? `<p class="sloped-entry">${slopedEntryBadgeHtml()}</p>` : ""}
      ${distanceLine(pool, loc)}
      ${pool.childrenOnly ? "" : sectionCard(pool, "main", showLabel)}
      ${pool.hasKiddiePool ? sectionCard(pool, "kiddie", showLabel) : ""}
      ${showSlide ? slideSection(pool) : ""}
    </article>`;
}

/**
 * Citywide summary header: an at-a-glance "open now" count. The per-card conflict
 * treatment ("Reported closed · though DPR says open") carries the staffing-gap
 * signal, so the former top "X reported closed though DPR says open" banner — and
 * the standalone "Conflicts only" filter — were removed.
 *
 * `total` (the denominator) is always computed over the FULL, unfiltered pool
 * set (see `summarizeOpen`), so toggling a filter never shifts "X of Y" — it
 * only ever appends a "· showing N" suffix when `counts.showing` is set (i.e.
 * active filters actually narrowed the visible set). Fixes 803ad825.
 */
function summaryHtml(counts: SummaryCounts): string {
  const showing = counts.showing !== undefined ? ` · showing ${counts.showing}` : "";
  return `<p class="summary-count">${counts.open} of ${counts.total} pools open now${showing}</p>`;
}

/**
 * Whether any pool-list filter is currently narrowing the set (vs. the
 * all-pools default). Drives the empty-state "Clear filters" affordance
 * (0c123585) — it only appears when a filter could plausibly be the reason
 * the list came up empty.
 */
function hasActiveFilters(filters: Filters): boolean {
  return filters.openNow || filters.sloped || filters.indoor !== "all";
}

/**
 * Empty-list message. When at least one filter is active, it gains a one-tap
 * "Clear filters" link back to the unfiltered list hash — a dead-end empty
 * state otherwise forces the user to hunt down and uncheck each filter by hand
 * (0c123585). `parseFilters` of an empty query string is the canonical
 * "no filters active" default.
 *
 * The name search (d8b45a64) narrows the same list, so it participates in the
 * same escape hatch: the control carries `data-action="clear-search"` and the
 * view clears the search box from it, meaning one tap always restores the full
 * list no matter which combination of filter + query emptied it. When the
 * search alone is responsible, the control becomes a plain "Clear search"
 * button (there is no filter hash to navigate back to).
 */
function emptyStateHtml(filters: Filters, search = ""): string {
  const searching = search.trim().length > 0;
  const msg = searching ? "No pools match your search." : "No pools match these filters.";
  if (hasActiveFilters(filters)) {
    const clearHref = filtersToHash(parseFilters(new URLSearchParams()), "list");
    return `<div class="empty"><p>${msg}</p><a class="clear-filters" href="${clearHref}" data-action="clear-search">Clear filters</a></div>`;
  }
  if (searching) {
    return `<div class="empty"><p>${msg}</p><button type="button" class="clear-filters" data-action="clear-search">Clear search</button></div>`;
  }
  return '<p class="empty">No pools match these filters.</p>';
}

function filterBar(filters: Filters): string {
  return `
    <section class="filters" aria-label="Filters">
      <label class="filter-toggle">
        <input type="checkbox" data-filter="open"${filters.openNow ? " checked" : ""} />
        Open now
      </label>
      <label class="filter-toggle">
        <input type="checkbox" data-filter="sloped"${filters.sloped ? " checked" : ""} />
        Sloped entry
      </label>
      <select class="filter-indoor" data-filter="indoor" aria-label="Indoor or outdoor">
        <option value="all"${filters.indoor === "all" ? " selected" : ""}>All</option>
        <option value="indoor"${filters.indoor === "indoor" ? " selected" : ""}>Indoor</option>
        <option value="outdoor"${filters.indoor === "outdoor" ? " selected" : ""}>Outdoor</option>
      </select>
    </section>`;
}

function applyStatus(root: HTMLElement, poolId: string, status: PoolStatus) {
  for (const s of status.sections) {
    const section = root.querySelector<HTMLElement>(
      `.section[data-pool="${CSS.escape(poolId)}"][data-section="${s.section}"]`,
    );
    if (!section) continue;
    // The slide reads Off/Running (defaults Off); other sections use the
    // confidence-led view. paintSectionStatus fills the word/qual/hero/accent and
    // the report-button labels in place, keeping the data-role hooks stable.
    paintSectionStatus(section, s.section === "slide" ? slideView(s) : sectionStatusView(s));
  }
  // Reliability is no longer rendered on the homepage cards (task 3ead41a8) — the
  // batch `/pools/status` payload omits it to cut a rollup Query per pool. It is
  // still shown on the detail view (src/views/detail.ts).
}

/**
 * Everything one paint of the card list needs. Bundled into a single object
 * because the list is re-rendered from several places (initial data, favorite
 * toggle, location fix, each debounced search keystroke) and a positional
 * argument list had grown long enough to invite mix-ups.
 */
interface ListRender {
  app: HTMLElement;
  /** The FULL unfiltered set — the stable "of Y" denominator. */
  base: Pool[];
  /** What actually renders: filters AND the name search already applied. */
  filtered: Pool[];
  filters: Filters;
  live: boolean;
  loc: LatLng | null;
  /** Current search query, for the search-aware empty state. */
  search: string;
  /** Reset the search box (wired onto the empty state's clear control). */
  onClearSearch: () => void;
}

/**
 * Render the pool cards into `.pool-list`, then (re)bind the per-card handlers
 * and paint statuses. Called once on initial render and again whenever a
 * favorite is toggled, so it must be safe to re-run: it rebuilds the section's
 * innerHTML (replacing old nodes and their listeners) before binding fresh
 * handlers, which avoids double-binding the report buttons.
 *
 * It also replaces the skeleton cards the chrome painted before the catalog
 * landed (18c5d959) — the first call is what turns the placeholder into data.
 */
function renderPoolList(ctx: ListRender): void {
  const { app, base, filtered, filters, live, loc, search } = ctx;
  const listEl = app.querySelector<HTMLElement>(".pool-list");
  if (!listEl) return;

  const sorted = sortPools(filtered, loc, getFavorites());
  listEl.innerHTML = sorted.length
    ? sorted.map((p) => poolCard(p, loc)).join("")
    : emptyStateHtml(filters, search);

  // Repaint the citywide summary from whatever statuses are cached right now.
  // Re-reading `cachedStatus` (rather than re-fetching) keeps this free to call
  // both on initial paint and again as each background status lands.
  const updateSummary = () => {
    const summaryEl = app.querySelector<HTMLElement>('[data-role="summary"]');
    if (!summaryEl) return;
    // Closed-for-season pools are not open/closed in the live sense, so exclude
    // them from the "N of M pools open now" open/total tally — `base` is the
    // full unfiltered set (a stable denominator). Fixes 803ad825: the
    // denominator no longer shifts when e.g. the Open now filter drops a
    // season-closed/unknown pool out of the visible set.
    const baseOpen = base.filter((p) => !p.closedForSeason);
    const counts = summarizeOpen(baseOpen, cachedStatus);
    // "· showing N" must match what's actually on screen — `sorted` (below)
    // renders every pool in `filtered`, INCLUDING closed-for-season ones (as
    // an informational card), e.g. Randall/Rosedale/Douglass still pass the
    // Sloped Entry filter. So `showing` counts all of `filtered`, not the
    // season-excluded subset — otherwise it undercounts the visible cards
    // whenever a season-closed pool survives a filter.
    if (filtered.length !== base.length) counts.showing = filtered.length;
    summaryEl.innerHTML = summaryHtml(counts);
  };
  updateSummary();

  // Favorite toggles: flip persisted state, then re-render the list so the
  // pool moves to/from the top. No URL change, so filters stay intact.
  listEl.querySelectorAll<HTMLButtonElement>('[data-action="favorite"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const card = btn.closest<HTMLElement>("[data-pool-card]");
      const poolId = card?.dataset.poolCard;
      if (!poolId) return;
      const fav = toggleFavorite(poolId);
      trackAction("favorite_toggle", { poolId, nowFavorited: fav });
      renderPoolList(ctx);
    });
  });

  // Empty-state escape hatch: whichever control the empty state rendered
  // ("Clear filters" link or "Clear search" button), tapping it also empties
  // the search box. The link additionally navigates, which re-renders the view
  // from scratch; clearing here is what makes the button work and what covers
  // the case where the link's href equals the current hash.
  listEl.querySelectorAll<HTMLElement>('[data-action="clear-search"]').forEach((el) => {
    el.addEventListener("click", () => ctx.onClearSearch());
  });

  // Report buttons (optimistic, shared with detail view). "Mark as Open"
  // submits immediately; "Mark as Closed" first reveals the inline reason
  // picker, and the actual submit happens when a reason chip (or "Skip") is
  // tapped — so the one-tap-closed path is preserved via "Skip". After a
  // successful "Mark as Open", the optional open-detail picker is revealed so
  // the reporter can enrich it (a second, rate-limit-exempt open report).
  listEl.querySelectorAll<HTMLButtonElement>('[data-action="report"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const section = btn.closest<HTMLElement>(".section")!;
      const poolId = section.dataset.pool!;
      const sectionName = section.dataset.section as PoolSection;
      const status = btn.dataset.status as "open" | "closed";
      const badge = section.querySelector<HTMLElement>('[data-role="status"]')!;
      const picker = section.querySelector<HTMLElement>('[data-role="reason-picker"]');
      if (status === "closed" && picker) {
        // Commit the closed status on the FIRST tap (durable even if the user
        // abandons the follow-up), THEN reveal the reason picker. Stash the
        // written row's ts so the reopen chip enriches that exact row. A
        // rate-limited/offline first tap (ts undefined) suppresses the pickers.
        void reportSection(poolId, sectionName, "closed", badge, (s) => applyStatus(app, poolId, s)).then(
          (ts) => {
            if (ts === undefined) return;
            section.dataset.reportTs = String(ts);
            picker.hidden = false;
          },
        );
        return;
      }
      if (picker) picker.hidden = true;
      void reportSection(poolId, sectionName, status, badge, (s) => applyStatus(app, poolId, s));
      // The plain open report is now in flight (it always lands — it's the first
      // tap, so the rate limiter clears it). Reveal the optional detail add-on.
      if (status === "open") {
        const detailPicker = section.querySelector<HTMLElement>('[data-role="open-detail-picker"]');
        if (detailPicker) detailPicker.hidden = false;
      }
    });
  });

  // Open-detail chips: send a SECOND open report carrying the chosen detail, then
  // collapse the picker. "Skip" (empty data-detail) just collapses it — the plain
  // open report already landed, so nothing more is sent. The follow-up is exempt
  // from the rate limiter server-side (it enriches the just-sent open report).
  listEl.querySelectorAll<HTMLButtonElement>('[data-role="open-detail-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const section = chip.closest<HTMLElement>(".section")!;
      const poolId = section.dataset.pool!;
      const sectionName = section.dataset.section as PoolSection;
      const badge = section.querySelector<HTMLElement>('[data-role="status"]')!;
      const detailPicker = section.querySelector<HTMLElement>('[data-role="open-detail-picker"]');
      const detail = (chip.dataset.detail || undefined) as OpenDetail | undefined;
      if (detailPicker) detailPicker.hidden = true;
      if (!detail) return;
      void reportSection(poolId, sectionName, "open", badge, (s) => applyStatus(app, poolId, s), undefined, undefined, detail);
    });
  });

  // Reason chips: stash the chosen reason (empty for "Skip" → no reason) on the
  // section, collapse the reason picker, and reveal the reopen-estimate row. The
  // actual submit happens when a reopen chip (or its "Skip") is tapped — so the
  // one-tap-closed path is preserved via reason-Skip then reopen-Skip.
  listEl.querySelectorAll<HTMLButtonElement>('[data-role="reason-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const section = chip.closest<HTMLElement>(".section")!;
      section.dataset.pendingReason = chip.dataset.reason || "";
      const reasonPicker = section.querySelector<HTMLElement>('[data-role="reason-picker"]');
      const reopenPicker = section.querySelector<HTMLElement>('[data-role="reopen-picker"]');
      if (reasonPicker) reasonPicker.hidden = true;
      if (reopenPicker) reopenPicker.hidden = false;
    });
  });

  // Reopen chips: ENRICH the already-committed closed report (stashed reportTs)
  // with the chosen reason + reopen estimate — no new report, no rollup. Skip+Skip
  // (both empty) sends nothing; the plain closed report already landed.
  listEl.querySelectorAll<HTMLButtonElement>('[data-role="reopen-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const section = chip.closest<HTMLElement>(".section")!;
      const poolId = section.dataset.pool!;
      const sectionName = section.dataset.section as PoolSection;
      const reopenPicker = section.querySelector<HTMLElement>('[data-role="reopen-picker"]');
      const reason = (section.dataset.pendingReason || undefined) as ClosureReason | undefined;
      const reopen = (chip.dataset.reopen || undefined) as ReopenEstimate | undefined;
      if (reopenPicker) reopenPicker.hidden = true;
      const targetTs = Number(section.dataset.reportTs);
      if (Number.isFinite(targetTs)) {
        void enrichClosedReport(poolId, sectionName, targetTs, (s) => applyStatus(app, poolId, s), reason, reopen);
      }
    });
  });

  // Expandable per-card history: collapsed by default, fetched lazily the first
  // time a card is opened (guarded by data-loaded). This avoids firing one
  // getRecentReports request per pool on initial render.
  listEl.querySelectorAll<HTMLDetailsElement>(".card-history").forEach((details) => {
    details.addEventListener("toggle", () => {
      if (!details.open || details.dataset.loaded) return;
      const section = details.closest<HTMLElement>(".section");
      const list = details.querySelector<HTMLElement>('[data-role="timeline"]');
      if (!section || !list) return;
      details.dataset.loaded = "1";
      const poolId = section.dataset.pool!;
      const sectionName = (section.dataset.section ?? "kiddie") as PoolSection;
      if (!live) {
        list.innerHTML = '<li class="muted">History unavailable offline.</li>';
        return;
      }
      getRecentReports(poolId, sectionName)
        .then((res) => renderTimeline(list, res.reports))
        .catch(() => {
          list.innerHTML = '<li class="muted">History unavailable offline.</li>';
        });
    });
  });

  // Paint any already-cached statuses immediately, then warm the rest with a
  // single batch request and repaint every card + the summary. Replaces the
  // former per-pool fan-out (one /status call per pool, throttled into multiple
  // waves), so the homepage LCP now waits on one fast call. Offline (sample
  // data) leaves the "…" badge as-is, matching prior behavior.
  for (const pool of sorted) {
    // Season-closed pools have no live pool status — but a waterslide one (Barry
    // Farm) still needs its slide section painted, so don't skip those.
    if (pool.closedForSeason && !pool.hasWaterslide) continue;
    const cached = cachedStatus(pool.poolId);
    if (cached) applyStatus(app, pool.poolId, cached);
  }
  if (live) {
    loadAllStatuses().then(() => {
      for (const pool of sorted) {
        if (pool.closedForSeason && !pool.hasWaterslide) continue;
        const status = cachedStatus(pool.poolId);
        if (status) applyStatus(app, pool.poolId, status);
      }
      updateSummary();
    });
  }
}

/**
 * The static chrome for the List tab: everything that depends only on `CITY`
 * and the parsed filters, i.e. everything that can be painted BEFORE the
 * catalog request resolves (18c5d959). The `.pool-list` is seeded with
 * skeleton cards and `data-role="dev-banner"` is left empty — those two are
 * the only pieces that need the fetch to have landed.
 */
function listChromeHtml(filters: Filters): string {
  return `
    <header class="topbar">
      <div class="topbar-head">
        <h1>${CITY.displayName}</h1>
        <p class="header-contact">${geoButtonHtml()}<a href="#/about">Contact</a><a href="${CITY.donateUrl}" target="_blank" rel="noopener">Donate</a></p>
      </div>
      <p class="tagline">${CITY.listTagline}</p>
      <p class="subhead">${CITY.listSubhead}</p>
      <p class="geo-banner" data-role="geo-banner" hidden></p>
      <div data-role="dev-banner"></div>
      ${viewTabs("list", filters)}
    </header>
    ${searchBoxHtml("Search pools by name")}
    ${filterBar(filters)}
    <main>
      <div class="summary" data-role="summary"></div>
      <div class="pool-list">${skeletonListHtml()}</div>
    </main>
    ${footerHtml()}`;
}

/** The offline/sample-data notice — only knowable once `loadPools` has answered. */
const DEV_BANNER_HTML = '<p class="dev-banner">Showing sample data. Backend not connected yet.</p>';

/**
 * Default List tab: every visible (kiddie-focused) pool, with the open-now
 * summary. Splash pads (`kind: "splash"`) are excluded here — they live on the
 * Map + Splash tabs — even though `loadPools` keeps them for those views.
 *
 * Rendered in two phases (18c5d959):
 *   1. Synchronously paint the chrome + skeleton cards and bind every control,
 *      so a slow `/api/pools` shows a real page instead of a blank blue screen.
 *   2. When the catalog lands, swap the skeletons for cards and fill in the
 *      pieces that needed the data (count line, offline banner, geo banner).
 */
export async function renderList(app: HTMLElement, query: URLSearchParams): Promise<void> {
  // Kick off the status batch concurrently with the pool catalog so the two
  // round-trips overlap on the uncached/mobile path. loadAllStatuses() fails
  // soft offline (catches internally), so firing early is safe even before we
  // know `live`. The dedup guard in store.ts ensures subsequent callers
  // (renderPoolList's repaint, the needsStatus await below) share this same
  // in-flight promise without issuing a second fetch.
  const statusP = loadAllStatuses();
  const filters = parseFilters(query);

  // --- Phase 1: instant chrome (nothing awaited yet) -----------------------
  app.innerHTML = listChromeHtml(filters);

  // View state phase 2 fills in. The handlers below are bound on the instant
  // chrome and close over these bindings, so a filter toggled (or a search
  // typed) before the data lands does the right thing once it does.
  let base: Pool[] = [];
  /** Filters applied, search NOT yet applied — the search narrows this. */
  let matching: Pool[] = [];
  let live = true;
  let ready = false;
  let search = "";
  // The user's location is opt-in (never auto-prompted). Seed from any fix made
  // earlier this session so List/Map/Splash share one grant; mutable because the
  // opt-in button (or a silent returning-user resolve) can set it later.
  let loc = cachedLocation();

  /** What should be on screen right now: filters AND the live search query. */
  const visible = (): Pool[] => matching.filter((p) => matchesQuery(p.name, search));

  const paint = (): void => {
    // No-op until the catalog has landed — the skeletons stay up meanwhile.
    if (!ready) return;
    renderPoolList({
      app,
      base,
      filtered: visible(),
      filters,
      live,
      loc,
      search,
      onClearSearch: () => searchBox.clear(),
    });
  };

  // Filter-as-you-type. Debounced, and deliberately NOT routed through the URL:
  // a hash change per keystroke re-runs the whole view (and scrolls to top).
  const searchBox = wireSearchBox(app, (q) => {
    search = q;
    paint();
  });

  // Filter controls update the URL (which re-renders via the router). They
  // navigate within the List tab so filters persist across List/Map.
  app.querySelector<HTMLInputElement>('[data-filter="open"]')?.addEventListener("change", (e) => {
    navigate(filtersToHash({ ...filters, openNow: (e.target as HTMLInputElement).checked }, "list"));
  });
  app.querySelector<HTMLInputElement>('[data-filter="sloped"]')?.addEventListener("change", (e) => {
    navigate(filtersToHash({ ...filters, sloped: (e.target as HTMLInputElement).checked }, "list"));
  });
  app.querySelector<HTMLSelectElement>('[data-filter="indoor"]')?.addEventListener("change", (e) => {
    const v = (e.target as HTMLSelectElement).value as Filters["indoor"];
    navigate(filtersToHash({ ...filters, indoor: v }, "list"));
  });

  // --- Location opt-in: "Use my location" ----------------------------------
  // We NEVER auto-prompt on first load. The button requests a fix on demand;
  // returning opted-in users resolve silently. A null fix (insecure context,
  // denial, timeout) degrades to an inline "unavailable" note, order unchanged.
  const geoBtn = app.querySelector<HTMLButtonElement>('[data-action="locate"]');
  const geoBanner = app.querySelector<HTMLElement>('[data-role="geo-banner"]');

  const paintBanner = (): void => {
    if (!geoBanner) return;
    if (!loc) {
      geoBanner.hidden = true;
      geoBanner.textContent = "";
      return;
    }
    const nearest = nearestPool(visible(), loc);
    geoBanner.textContent = nearest
      ? `Closest: ${nearest.pool.name} (${milesAwayLabel(nearest.meters)})`
      : "";
    geoBanner.hidden = !nearest;
  };

  const onFix = (fix: LatLng | null): void => {
    if (fix) {
      loc = fix;
      if (geoBtn) {
        geoBtn.textContent = GEO_LABEL_LOCATED;
        geoBtn.disabled = true;
      }
      // A fix that beats the catalog home just updates `loc`; `paint()` is a
      // no-op until the data lands and then renders nearest-first anyway.
      paint();
      paintBanner();
    } else {
      if (geoBtn) {
        geoBtn.textContent = GEO_LABEL_IDLE;
        geoBtn.disabled = false;
      }
      if (geoBanner) {
        // A blocked permission is actionable (the user can re-allow it), so give
        // explicit steps; other failures (timeout/unavailable/unsupported) just
        // degrade to "showing all".
        geoBanner.textContent =
          lastLocationError() === "denied" ? GEO_DENIED_COPY : "Location unavailable. Showing all pools.";
        geoBanner.hidden = false;
      }
    }
  };

  geoBtn?.addEventListener("click", () => {
    geoBtn.disabled = true;
    geoBtn.textContent = GEO_LABEL_LOCATING;
    void getUserLocation().then(onFix);
  });

  if (loc) {
    // A session fix already exists (set on a prior tab) — reflect it, no re-prompt.
    if (geoBtn) {
      geoBtn.textContent = GEO_LABEL_LOCATED;
      geoBtn.disabled = true;
    }
  } else if (hasLocationOptIn()) {
    // Returning opted-in user: resolve silently (the browser won't re-prompt once
    // permission is granted); a first-time visitor is never auto-prompted.
    void getUserLocation().then(onFix);
  }

  // --- Phase 2: the catalog lands ------------------------------------------
  const { pools, live: isLive } = await loadPools();
  live = isLive;
  base = pools.filter((p) => p.kind !== "splash");

  // Open-now filters on live status, so we need the cache warmed before
  // filtering. Await the in-flight promise started above (not a fresh call)
  // to avoid a second fetch.
  const needsStatus = filters.openNow;
  if (needsStatus && live) {
    await statusP;
  }

  matching = base.filter((p) => {
    if (!matchesStaticFilters(p, filters)) return false;
    if (filters.openNow && !poolIsOpenNow(cachedStatus(p.poolId))) return false;
    return true;
  });
  ready = true;

  // The offline/sample-data notice is only knowable post-fetch, so it fills the
  // placeholder the chrome left in the header.
  const devBanner = app.querySelector<HTMLElement>('[data-role="dev-banner"]');
  if (devBanner) devBanner.innerHTML = live ? "" : DEV_BANNER_HTML;

  // Cards (favorites + optional nearest-first), their handlers, and status
  // paints — this is the call that replaces the skeletons. Favoriting a pool
  // re-runs it against the same set; a filtered-out pool can never reappear
  // because the sort happens after filtering.
  paint();
  paintBanner();
}
